import { createRouter, createWebHistory } from "vue-router";
import { Capacitor } from "@capacitor/core";
import adminRoutes from "./adminRoutes";
import userRoutes from "./userRoutes";
import sitioWebRoutes from "./sitioWebRoutes";
import torneosRoutes from "./torneosRoutes";
import feriasRoutes from "./feriasRoutes";
import kermesesRoutes from "./kermesesRoutes";
import rifasRoutes, { publicRifasRoutes } from "./rifasRoutes";
import materialGraficoRoutes from "./materialGraficoRoutes";
import proveedoresRoutes from "./proveedoresRoutes";
import clasificadosRoutes from "./clasificadosRoutes";
import galeriasRoutes from "./galeriasRoutes";
import bancoFraternoRoutes from "./bancoFraternoRoutes";
import convivenciaRoutes from "./convivenciaRoutes";
import deportesRoutes from "./deportesRoutes";
import comisionesRoutes from "./comisionesRoutes";
import notificacionesEmailRoutes from "./notificacionesEmailRoutes";
import tareasRoutes from "./tareasRoutes";
import surveysRoutes, { surveyRespondRoute } from "./surveysRoutes";
import kioscoRoutes from "./kioscoRoutes";
import calendarRoutes from "./calendarRoutes";
import eventosRoutes from "./eventosRoutes";
import publicTorneosRoutes from "./publicTorneosRoutes";
import {
  isAuthenticated,
  navigationGuard,
} from "./guards/navigationGuard";
import { resolveAuthenticatedResumeRoute } from "@/navigation/bottomNavVisibility";
import { useCursoStore } from "@/store/cursoStore";

const resolveResumePath = () => {
  const cursoStore = useCursoStore();
  return resolveAuthenticatedResumeRoute(
    localStorage.getItem("lastRoute"),
    cursoStore.hasCurso(),
  );
};

const routes = [
  {
    path: "/",
    redirect: (to) => {
      console.log("to", to);
      return isAuthenticated() ? resolveResumePath() : "/login";
    },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/LoginPage.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
    beforeEnter: (to, from, next) => {
      // Persist continue ASAP: si hay sesión, LoginPage hace el bridge al CMS.
      const continueRaw = String(
        to.query.continue ?? to.query.return_url ?? ""
      ).trim();
      if (continueRaw) {
        try {
          sessionStorage.setItem(
            "authContinueUrl",
            decodeURIComponent(continueRaw)
          );
        } catch {
          sessionStorage.setItem("authContinueUrl", continueRaw);
        }
      }

      if (isAuthenticated()) {
        // Con ?continue= no redirigir al dashboard: hay que volver al CMS.
        if (continueRaw || sessionStorage.getItem("authContinueUrl")) {
          next();
          return;
        }
        next(resolveResumePath());
        return;
      }
      next();
    },
  },
  {
    path: "/register",
    name: "register",
    component: () => import("@/views/RegisterPage.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/legal/terminos",
    name: "legal-terms",
    component: () => import("@/views/legal/LegalTermsView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/legal/privacidad",
    name: "legal-privacy",
    component: () => import("@/views/legal/LegalPrivacyView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/validar-qr",
    name: "validar-qr",
    component: () => import("@/views/ValidarQrView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/email-preferences",
    name: "email-preferences",
    component: () => import("@/views/email/EmailPreferencesView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/email-preferences/unsubscribe",
    name: "email-preferences-unsubscribe",
    component: () => import("@/views/email/EmailUnsubscribeView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/membership/review",
    name: "membership-review",
    component: () => import("@/views/email/MembershipReviewView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  ...adminRoutes,
  sitioWebRoutes,
  torneosRoutes,
  feriasRoutes,
  kermesesRoutes,
  rifasRoutes,
  ...publicRifasRoutes,
  materialGraficoRoutes,
  proveedoresRoutes,
  clasificadosRoutes,
  galeriasRoutes,
  bancoFraternoRoutes,
  convivenciaRoutes,
  deportesRoutes,
  comisionesRoutes,
  notificacionesEmailRoutes,
  tareasRoutes,
  ...surveysRoutes,
  surveyRespondRoute,
  kioscoRoutes,
  calendarRoutes,
  eventosRoutes,
  ...publicTorneosRoutes,
  ...userRoutes,
  {
    path: "/convivencia/asistencia/:token",
    name: "convivencia-asistencia",
    component: () => import("@/views/ConvivenciaAsistenciaView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    redirect: (to) => {
      console.log("Ruta no encontrada:", to.path);
      return isAuthenticated()
        ? "/" // Si está autenticado, redirigir a la pantalla principal
        : "/login"; // Si no está autenticado, redirigir al login
    },
  },
  {
    path: "/invite/extra/:token",
    name: "invite-extra-validate",
    component: () => import("@/views/InviteExtraValidateView.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/deep-link-test",
    name: "deep-link-test",
    component: () => import("@/views/DeepLinkTestView.vue"),
  },
  {
    path: "/callback",
    name: "callback",
    component: () => import("@/views/CallbackPage.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/user/menu-cpa",
    name: "menu-cpa",
    component: () => import("../views/user/MenuCPA.vue"),
    meta: {
      requiresAuth: true,
      requiresCurso: true,
      appSection: "activities",
    },
  },
  {
    path: "/magic-link",
    name: "magic-link",
    component: () => import("@/views/MagicLink.vue"),
    meta: { hideBottomNav: true, hideHeader: true, requiresAuth: false },
  },
  {
    path: "/agregar-apoderado-curso",
    name: "agregar-apoderado-curso",
    component: () => import("@/views/AgregarApoderadoACurso.vue"),
  },
  {
    path: "/agregar-usuario-grupo",
    name: "agregar-usuario-grupo",
    component: () => import("@/views/AgregarUsuarioAGrupo.vue"),
  },
  {
    path: "/agregar-directivas-colegio/:codigo?",
    name: "agregar-directivas-colegio",
    component: () => import("@/views/AgregarDirectivasAColegio.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Autenticación, admin plataforma, curso seleccionado y roles (navigationGuard)
router.beforeEach(navigationGuard);

router.afterEach((to) => {
  if (to.name !== "login" && to.name !== "validar-qr") {
    //console.log("to", to);
    localStorage.setItem("lastRoute", to.fullPath);
  }
});

export default router;
